// music-player.ts — encounter music playlist player with equal-power crossfade.
//
// RESOLVED IN THE BROWSER (owner 2026-09-10, plans/ft3d-client-music-handoff-2026-09-10.md): the back
// end only saves music state and publishes it on the existing WS frames; music-resolver.ts decides the
// playlist from the WS-fed campaign/encounter caches + the music catalog (music-catalog.ts, one fetch
// per catalog revision). There is no per-change music request. Design: plans/music-playlist-player.md.
//
// WHY TWO NEW HIDDEN DECKS INSTEAD OF REUSING audio_player_music_1:
// template.js:9638 persists that element's volume to a cookie on every `volumechange`.
// Ramping its volume to crossfade would overwrite the user's saved music level with the
// fade value — they'd come back next session to silent music. So music_1 is left ENTIRELY
// alone and keeps its existing jobs (GM soundboard preview, the `mx` roll-audio channel,
// and the volume/mute UI). It is now also the MASTER: the decks multiply their crossfade
// gain by music_1's volume, so the user's slider still governs playlist loudness.
//
// WHY NOT WEB AUDIO: there is no AudioContext in production. Routing these through
// MediaElementSource would require CORS headers on the CDN and would bypass the
// per-channel volume/mute cookie system above. An equal-power ramp on
// HTMLAudioElement.volume is entirely adequate for a 10 s music fade.
//
// Everything here is defensive: music must never break the WS handlers that call it.

import { roomTypeEntry } from '../../../ft3d/engine/ft3d-creation-core.js';
import { ftMusicCatalog, ftMusicCatalogEnsureRev, ftMusicCatalogRefreshOnMiss } from './music-catalog';
import {
    ftMusicCreatorProgram, ftNormalizeRoomMusic, ftResolveViewerMusic,
    type FtMusicCatalog, type FtMusicResolved, type FtMusicTrack,
} from './music-resolver';
import { ftCampaignAudioSurface } from '../shared/audio-surface';

/** The globals this player reads. Loose on purpose: several bundles declare FateTide differently. */
interface FtMxWindow {
    ftGetActiveEncounterId?: () => string;
    ftGetCampaignId?: () => string;
    checkIfValidUUID?: (s: string) => boolean;
    getEncounterArr?: (id: string) => Promise<unknown>;
    FateTide?: {
        campaign?: { id?: string } | null;
        campaignArr?: unknown;
        campaignCache?: { get?: (id: string) => unknown };
        encounterCache?: { get?: (id: string) => unknown };
    };
}

const FT_MX_DECK_IDS = ['audio_player_music_deck_a', 'audio_player_music_deck_b'] as const;
const FT_MX_MASTER_ID = 'audio_player_music_1';
const FT_MX_FADE_TICK_MS = 50;

let ftMxKey = '';                  // mode|order_key — identity of the CURRENT playlist
let ftMxTracks: FtMusicTrack[] = [];
let ftMxIndex = -1;
let ftMxActive = 0;                // index into FT_MX_DECK_IDS
let ftMxCrossfadeMs = 6000;
let ftMxFadeTimer: ReturnType<typeof setInterval> | null = null;
let ftMxNextTimer: ReturnType<typeof setTimeout> | null = null;
let ftMxGain = [1, 0];             // per-deck crossfade gain, BEFORE master
let ftMxSuspended = false;         // true while the GM previews on music_1
let ftMxUnlockArmed = false;
let ftMxInFlight = false;
// A sync asked for while another was in flight. Coalescing: many triggers during one request drain
// to a SINGLE follow-up resolve, which then sees the newest state. See ftSyncViewerMusic's guard.
let ftMxResyncQueued = false;
// LOCAL ("just me") theme. Client-only: never written to the DB, never broadcast, and it
// outranks the server's answer for this viewer alone. Survives reloads via sessionStorage so
// a GM auditioning a theme doesn't lose it on navigation, but dies with the tab — a local
// override is a working state, not a setting.
let ftMxLocalBucket: string | null = null;
// CREATOR PROGRAM mode (2026-08-26). The character creator has no encounter, so the whole
// encounter/campaign resolve below is inapplicable — ftMxResolve() builds the creator program
// (ftMusicCreatorProgram) from the chosen ancestry instead. Everything DOWNSTREAM of
// the resolve (decks, equal-power crossfade, skip/prev, shuffle, master volume, the ft-music-changed
// announce) is shared, which is the entire reason this is a branch here rather than a second player
// inside the creator bundle.
//
// Not persisted anywhere: the creator sets it on mount and clears it on unload. A stale creator
// flag leaking into an encounter tab would silence that tab's music.
let ftMxCreatorMode = false;
let ftMxCreatorAncestry = '';
let ftMxCreatorCharacter = '';
// The ancestry's NAME — the program derives its buckets from it ("Elf, Wood" -> elvish_wood).
let ftMxCreatorAncestryName = '';
// The room the listener is in, from the FT3D scene's `ft3d-room-ambience` postMessage (the same
// listener position ambience follows: the active caster, else the camera). Only the room id is read;
// its saved music comes from the parent's WS-current encounter blob, never the scene's draft.
let ftMxRoom: { encounterId: string; roomId: string | null; source: MessageEventSource | null } | null = null;
// The last playable answer the resolver gave (read-only diagnostics: ftMusicResolvedState, smokes).
let ftMxLastResolved: FtMusicResolved | null = null;
// Parsed encounter_shape, keyed by the raw string so an unchanged shape is not re-parsed per sync.
let ftMxShape: { raw: string; boxes: Array<{ id?: unknown; creation?: { music?: unknown; function?: unknown } }> } | null = null;
// PAUSED is sticky: without it the next WS frame (a combat flip, a clock tick) would resolve and
// start the music straight back up, which reads as "pause doesn't work". Session-scoped like the
// local override — a pause is working state, not a setting.
let ftMxPaused = false;
// Bucket of the playlist currently loaded, whatever mode put it there (auto / combat /
// GM broadcast / local). The music form highlights this row, so it must reflect what is
// ACTUALLY playing rather than just the local override.
let ftMxCurrentBucket: string | null = null;
// Repeat the CURRENT track. The playlist already wraps end-to-start on its own, so a "loop"
// control that looped the playlist would do nothing visible -- repeat-one is the behaviour
// that isn't already there.
let ftMxLoopOne = false;
// SHUFFLE IS A PLAY ORDER, NOT A SHUFFLED ARRAY. `ftMxTracks` keeps the server's order — which
// is already a DETERMINISTIC per-bucket shuffle (ft_music_bucket_tracks hashes order_key+audio_id
// so every client at the table hears the same running order) — and `ftMxOrder` is a permutation
// of its indices that the transport walks. Reordering ftMxTracks itself would renumber the
// visible track list under the GM and desync "track 3 of 9" from what everyone else sees.
//
// So the two shuffles are different things and both are wanted: the server's makes a tavern open
// on the same track for the whole table, this one lets ONE listener stop hearing that order.
let ftMxShuffle = false;
let ftMxOrder: number[] = [];
let ftMxCursor = 0;
// Deck fading IN during a crossfade, else null. ftMxActive only flips once the fade COMPLETES,
// so for the whole fade it points at the outgoing deck — which is exactly why the now-playing
// strip used to blank itself for the entire crossfade: it asked a deck that was paused (or on
// its way out) whether anything was playing, while ftMxIndex had already moved to the new track.
let ftMxIncoming: number | null = null;

function ftMxPlaybackSuppressed(): boolean {
    try {
        const params = new URLSearchParams(window.location.search);
        return params.get('ft3d_campaign_view') === '1' || params.get('ft3d_embed') === '1';
    } catch {
        return false;
    }
}

function ftMxLog(payload: Record<string, unknown>): void {
    try {
        const w = window as unknown as { ftDebugLog?: (c: string, p: unknown) => void };
        if (typeof w.ftDebugLog === 'function') w.ftDebugLog('audio', { mx: true, ...payload });
    } catch (e) { /* never throw from logging */ }
}

function ftMxEl(id: string): HTMLAudioElement | null {
    return document.getElementById(id) as HTMLAudioElement | null;
}

function ftMxDeck(i: number): HTMLAudioElement | null {
    const id = FT_MX_DECK_IDS[i];
    return id ? ftMxEl(id) : null;
}

function ftMxFmtTime(s: number): string {
    if (!isFinite(s) || s < 0) return '0:00';
    const m = Math.floor(s / 60);
    const r = Math.floor(s - m * 60);
    return m + ':' + (r < 10 ? '0' : '') + r;
}

/**
 * Paint the "now playing" strip in the soundboard. The decks themselves are hidden — a
 * crossfade means two elements playing at once, so their native controls would be two players
 * disagreeing — but a playlist with no visible state reads as broken, since the master player
 * above sits idle the whole time music is running.
 */
/**
 * Tell any open UI that playback state changed. The music form used to guess with
 * setTimeout(60/300) and would repaint BEFORE the async resolve had loaded the new playlist,
 * so the highlighted row lagged a selection behind.
 */
function ftMxAnnounce(): void {
    try { document.dispatchEvent(new CustomEvent('ft-music-changed')); }
    catch (e) { /* never throw at a listener */ }
}

let ftMxListKey = '';

/**
 * Playlist under the transport. Rebuilt only when the track SET changes (keyed on bucket +
 * length) — the highlight moves without touching the DOM, so this is not re-rendering ~20 rows
 * on every 1 Hz tick.
 */
function ftMxRenderTrackList(): void {
    const host = document.getElementById('ft_mx_tracklist');
    if (!host) return;

    if (!ftMxTracks.length) { host.classList.add('d-none'); host.innerHTML = ''; ftMxListKey = ''; return; }
    host.classList.remove('d-none');

    const key = (ftMxCurrentBucket || '') + '|' + ftMxTracks.length;
    if (key !== ftMxListKey) {
        ftMxListKey = key;
        host.innerHTML = ftMxTracks.map((t, i) => (
            '<button type="button" class="ft-mx-track btn btn-sm w-100 text-start border-0 rounded-0 px-2 py-1 d-flex gap-2"'
            + ' data-ft-mx-track="' + i + '">'
            +   '<span class="text-muted small flex-shrink-0" style="min-width:1.6rem">' + (i + 1) + '</span>'
            +   '<span class="small text-truncate flex-grow-1">'
            +     String(t.display_name || t.audio_name || '').replace(/[&<>"]/g, (c) => (
                    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] || c))
            +   '</span>'
            + '</button>'
        )).join('');
    }

    // Highlight pass — cheap, runs every repaint.
    const rows = host.querySelectorAll('[data-ft-mx-track]');
    for (let i = 0; i < rows.length; i++) {
        const el = rows[i] as HTMLElement;
        const on = i === ftMxIndex;
        el.style.backgroundColor = on ? 'rgba(25,135,84,.28)' : '';
        el.style.boxShadow = on ? 'inset 3px 0 0 rgba(25,135,84,.95)' : '';
        el.setAttribute('aria-current', on ? 'true' : 'false');
    }
}

function ftMxUpdateNowPlaying(): void {
    const wrap = document.getElementById('ft_mx_now_playing');
    if (!wrap) return;

    const track = ftMxTracks[ftMxIndex];
    // During a crossfade the CURRENT track lives on the incoming deck; ftMxActive is still the
    // outgoing one until the fade lands.
    const deck = ftMxDeck(ftMxIncoming ?? ftMxActive);
    const other = ftMxDeck(ftMxIncoming === null ? -1 : ftMxActive);
    // Either deck audibly playing counts as "playing" — mid-fade both are. A fade IN FLIGHT
    // also counts even if neither element reports playing yet: decks are preload="none", so a
    // freshly-src'd deck stays .paused for as long as the network takes, and treating that as
    // "nothing playing" is what made the strip flicker out at a transition.
    const fading = ftMxIncoming !== null;
    const playing = !!track && (fading || (!!deck && !deck.paused) || (!!other && !other.paused));

    if (!track || (!playing && !ftMxPaused)) {
        wrap.classList.add('d-none');
        const tl = document.getElementById('ft_mx_tracklist');
        if (tl) tl.classList.add('d-none');
        return;
    }
    wrap.classList.remove('d-none');

    const title = document.getElementById('ft_mx_np_title');
    if (title) {
        const n = ftMxTracks.length;
        // The trailing (i/n) is the QUEUE POSITION, not the take letter — the take letter is
        // already inside display_name, so a track can legitimately read "… (B)  (3/12)".
        title.textContent = (track.display_name || track.audio_name)
            + (n > 1 ? '  (' + (ftMxIndex + 1) + '/' + n + ')' : '');
    }

    const dur = (track.audio_seconds && track.audio_seconds > 0)
        ? track.audio_seconds
        : (deck && isFinite(deck.duration) ? deck.duration : 0);
    const pos = deck ? deck.currentTime : 0;

    const time = document.getElementById('ft_mx_np_time');
    if (time) time.textContent = ftMxPaused ? 'paused' : (ftMxFmtTime(pos) + ' / ' + ftMxFmtTime(dur));

    const pct = dur > 0 ? Math.max(0, Math.min(100, (pos / dur) * 100)) : 0;
    const bar = document.getElementById('ft_mx_np_bar');
    if (bar) bar.style.width = pct + '%';
    const seekEl = bar ? bar.closest('[data-ft-mx-ctl="seek"]') : null;
    if (seekEl) seekEl.setAttribute('aria-valuenow', String(Math.round(pct)));

    const loopBtn = document.getElementById('ft_mx_np_loop');
    if (loopBtn) {
        loopBtn.classList.toggle('btn-primary', ftMxLoopOne);
        loopBtn.classList.toggle('btn-outline-secondary', !ftMxLoopOne);
        loopBtn.setAttribute('aria-pressed', ftMxLoopOne ? 'true' : 'false');
        loopBtn.setAttribute('title', ftMxLoopOne ? 'Repeat track: on' : 'Repeat track: off');
    }

    const shufBtn = document.getElementById('ft_mx_np_shuffle');
    if (shufBtn) {
        shufBtn.classList.toggle('btn-primary', ftMxShuffle);
        shufBtn.classList.toggle('btn-outline-secondary', !ftMxShuffle);
        shufBtn.setAttribute('aria-pressed', ftMxShuffle ? 'true' : 'false');
        shufBtn.setAttribute('title', ftMxShuffle ? 'Shuffle: on' : 'Shuffle: off');
    }

    // The soundboard's own music element is hidden until IT is what's playing (a GM preview or
    // an `mx` roll) -- otherwise it is a dead 0:00/0:00 player sitting above this strip.
    const master = ftMxMaster();
    if (master) master.classList.toggle('d-none', master.paused);

    ftMxRenderTrackList();

    const toggle = document.getElementById('ft_mx_np_toggle');
    if (toggle) {
        toggle.innerHTML = ftMxPaused ? '&#9654;' : '&#10073;&#10073;';
        toggle.setAttribute('title', ftMxPaused ? 'Resume' : 'Pause');
        toggle.setAttribute('aria-label', ftMxPaused ? 'Resume music' : 'Pause music');
    }
}

function ftMxMaster(): HTMLAudioElement | null {
    return ftMxEl(FT_MX_MASTER_ID);
}

/** User's music level. Mute or a missing master both mean "silent", not "full blast". */
function ftMxMasterVolume(): number {
    const m = ftMxMaster();
    if (!m) return 0;
    if (m.muted) return 0;
    const v = Number(m.volume);
    return isNaN(v) ? 0 : Math.max(0, Math.min(1, v));
}

function ftMxApplyGain(): void {
    const master = ftMxMasterVolume();
    for (let i = 0; i < FT_MX_DECK_IDS.length; i++) {
        const d = ftMxDeck(i);
        if (!d) continue;
        const g = Math.max(0, Math.min(1, ftMxGain[i] ?? 0));
        d.volume = Math.max(0, Math.min(1, g * master));
    }
}

function ftMxClearTimers(): void {
    if (ftMxFadeTimer !== null) { clearInterval(ftMxFadeTimer); ftMxFadeTimer = null; }
    if (ftMxNextTimer !== null) { clearTimeout(ftMxNextTimer); ftMxNextTimer = null; }
}

/**
 * Autoplay: a deck the user has never touched may reject play(). Arm a one-shot gesture
 * listener and retry, rather than silently never starting.
 */
function ftMxPlay(deck: HTMLAudioElement): void {
    if (ftMxPlaybackSuppressed()) {
        try { deck.pause(); } catch (e) { /* already stopped */ }
        return;
    }
    try {
        const p = deck.play() as unknown as Promise<void> | undefined;
        if (p && typeof p.catch === 'function') {
            p.catch(() => {
                ftMxLog({ phase: 'autoplay-blocked', armed: ftMxUnlockArmed });
                if (ftMxUnlockArmed) return;
                ftMxUnlockArmed = true;
                const retry = () => {
                    ftMxUnlockArmed = false;
                    document.removeEventListener('click', retry, true);
                    document.removeEventListener('keydown', retry, true);
                    try { void deck.play(); } catch (e) { /* give up quietly */ }
                };
                document.addEventListener('click', retry, true);
                document.addEventListener('keydown', retry, true);
            });
        }
    } catch (e) {
        ftMxLog({ phase: 'play-threw', error: String(e) });
    }
}

/** Equal-power crossfade from the active deck to `to`, then advance bookkeeping. */
function ftMxCrossfadeTo(toDeckIdx: number, startAt: number): void {
    ftMxClearTimers();
    const from = ftMxActive;
    const steps = Math.max(1, Math.round(ftMxCrossfadeMs / FT_MX_FADE_TICK_MS));
    let step = 0;

    const toDeck = ftMxDeck(toDeckIdx);
    if (!toDeck) return;
    ftMxIncoming = toDeckIdx;
    toDeck.currentTime = startAt;
    ftMxGain[toDeckIdx] = 0;
    ftMxApplyGain();
    ftMxPlay(toDeck);

    ftMxFadeTimer = setInterval(() => {
        step++;
        const p = Math.min(1, step / steps);
        // Equal power: sin/cos keep perceived loudness ~constant through the blend.
        // A linear ramp dips ~3 dB in the middle, which is exactly the audible hole
        // the 8-10 s crossfade requirement exists to avoid.
        ftMxGain[toDeckIdx] = Math.sin((p * Math.PI) / 2);
        ftMxGain[from] = Math.cos((p * Math.PI) / 2);
        ftMxApplyGain();

        if (p >= 1) {
            if (ftMxFadeTimer !== null) { clearInterval(ftMxFadeTimer); ftMxFadeTimer = null; }
            const fromDeck = ftMxDeck(from);
            if (fromDeck) { try { fromDeck.pause(); } catch (e) { /* ignore */ } }
            ftMxGain[from] = 0;
            ftMxApplyGain();
            ftMxActive = toDeckIdx;
            ftMxIncoming = null;
            ftMxScheduleNext();
            ftMxUpdateNowPlaying();
            ftMxAnnounce();
        }
    }, FT_MX_FADE_TICK_MS);
}

/** Arm the handoff to the next track at (duration - crossfade). */
function ftMxScheduleNext(): void {
    if (ftMxNextTimer !== null) { clearTimeout(ftMxNextTimer); ftMxNextTimer = null; }
    if (ftMxTracks.length < 2) return;   // single track loops; nothing to schedule

    const deck = ftMxDeck(ftMxActive);
    const track = ftMxTracks[ftMxIndex];
    if (!deck || !track) return;

    // audio_seconds comes from the MusicForge bucket manifest, so we can schedule without
    // waiting on loadedmetadata. Fall back to the element if it's ever missing.
    const durSec = (track.audio_seconds && track.audio_seconds > 0)
        ? track.audio_seconds
        : (isFinite(deck.duration) && deck.duration > 0 ? deck.duration : 0);
    if (durSec <= 0) return;             // 'ended' is the safety net

    const leadMs = Math.max(1000, (durSec * 1000) - ftMxCrossfadeMs - (deck.currentTime * 1000));
    ftMxNextTimer = setTimeout(() => ftMxAdvance(), leadMs);
}

/** Load whatever ftMxIndex points at onto the free deck and crossfade to it. */
function ftMxPlayCurrentIndex(why: string): void {
    const next = ftMxTracks[ftMxIndex];
    // Cross-fade AWAY from whichever deck is currently carrying audio -- mid-fade that is the
    // incoming one, not ftMxActive.
    const fromIdx = ftMxIncoming ?? ftMxActive;
    const toIdx = fromIdx === 0 ? 1 : 0;
    const toDeck = ftMxDeck(toIdx);
    if (!toDeck || !next) return;

    toDeck.loop = false;
    if (toDeck.src !== next.audio_file) toDeck.src = next.audio_file;
    ftMxLog({ phase: why, to: next.audio_name, deck: toIdx });
    ftMxCrossfadeTo(toIdx, 0);
}

/**
 * Rebuild `ftMxOrder` for the current playlist, keeping `keep` current (-1 = start fresh).
 *
 * Toggling shuffle mid-song moves the running track to the front of the new order rather than
 * leaving it wherever it fell, so the button never interrupts what the table is listening to —
 * only what comes next changes.
 */
function ftMxBuildOrder(keep: number): void {
    const n = ftMxTracks.length;
    ftMxOrder = [];
    for (let i = 0; i < n; i++) ftMxOrder.push(i);

    if (ftMxShuffle && n > 1) {
        // Fisher-Yates, not sort-by-random: the latter is biased AND non-transitive, which some
        // engines answer with an exception rather than a merely-wrong order.
        for (let i = n - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            const t = ftMxOrder[i] as number;
            ftMxOrder[i] = ftMxOrder[j] as number;
            ftMxOrder[j] = t;
        }
        if (keep >= 0 && keep < n) {
            const at = ftMxOrder.indexOf(keep);
            if (at > 0) { ftMxOrder.splice(at, 1); ftMxOrder.unshift(keep); }
        }
        ftMxCursor = 0;
        return;
    }

    ftMxCursor = (keep >= 0 && keep < n) ? keep : 0;
}

/** Point the cursor at whatever ftMxIndex is — after a pick from the track list. */
function ftMxSyncCursor(): void {
    if (ftMxOrder.length !== ftMxTracks.length) { ftMxBuildOrder(ftMxIndex); return; }
    const at = ftMxOrder.indexOf(ftMxIndex);
    ftMxCursor = at >= 0 ? at : 0;
}

function ftMxAdvance(step?: number): void {
    if (!ftMxTracks.length) return;
    const n = ftMxTracks.length;
    // Walk the PLAY ORDER. With shuffle off it is the identity, so this is the old behaviour.
    if (ftMxOrder.length !== n) ftMxBuildOrder(ftMxIndex);
    // An EXPLICIT step (Next / Prev) now outranks repeat-one; only an AUTOMATIC advance is
    // pinned by it. Previously loop-one won outright, so with repeat on, Next replayed the same
    // track and Prev went back TWO (it pre-subtracted 2 and relied on advance(1) adding one,
    // which repeat-one then swallowed). Same rule the public page's player already uses.
    const delta = (typeof step === 'number') ? step : (ftMxLoopOne ? 0 : 1);
    ftMxCursor = ((ftMxCursor + delta) % n + n) % n;
    ftMxIndex = ftMxOrder[ftMxCursor] ?? 0;
    ftMxPlayCurrentIndex('advance');
}

/** Jump straight to a track from the playlist list. */
function ftMusicPlayIndex(i: number): void {
    if (!ftMxTracks.length) return;
    const n = ftMxTracks.length;
    const target = ((Math.trunc(i) % n) + n) % n;
    // A pick is an unmistakable "play this", so it lifts a pause the way choosing a theme does.
    if (ftMxPaused) {
        ftMxPaused = false;
        try { sessionStorage.removeItem('ft_mx_paused'); } catch (e) { /* ignore */ }
    }
    ftMxIndex = target;
    // Resume the shuffled order FROM the picked track; without this the pick plays and then the
    // playlist jumps to wherever the cursor had got to.
    ftMxSyncCursor();
    ftMxPlayCurrentIndex('pick');
}

/** Hard start (no crossfade in from silence — fade in from 0 on the incoming deck only). */
function ftMxStart(resolved: FtMusicResolved): void {
    ftMxClearTimers();
    ftMxTracks = resolved.tracks;
    ftMxCrossfadeMs = resolved.crossfade_ms > 0 ? resolved.crossfade_ms : 6000;
    ftMxCurrentBucket = resolved.bucket || null;
    // -1: a newly resolved theme has no track to preserve, so shuffle picks the OPENER too. A
    // shuffle that always starts on track 1 and only randomises from the second song is the
    // classic broken-shuffle tell — and for a combat bed the first track is the one you hear most.
    ftMxBuildOrder(-1);
    ftMxIndex = ftMxOrder[0] ?? 0;

    const other = ftMxActive === 0 ? 1 : 0;
    const toDeck = ftMxDeck(other);
    const track = ftMxTracks[ftMxIndex];
    if (!toDeck || !track) return;

    // One track (GM override) loops; a real playlist advances.
    toDeck.loop = ftMxTracks.length === 1;
    toDeck.src = track.audio_file;
    ftMxLog({ phase: 'start', mode: resolved.mode, bucket: resolved.bucket,
              count: ftMxTracks.length, first: track.audio_name });
    ftMxAnnounce();
    ftMxCrossfadeTo(other, 0);
}

function ftMxStopAll(): void {
    ftMxClearTimers();
    ftMxIncoming = null;
    for (let i = 0; i < FT_MX_DECK_IDS.length; i++) {
        const d = ftMxDeck(i);
        if (d) { try { d.pause(); } catch (e) { /* ignore */ } }
        ftMxGain[i] = 0;
    }
    ftMxApplyGain();
}

/**
 * The viewer's encounter: the ACTIVE encounter first (caster-scoped on the campaign page, and the exact
 * key the WS-fed encounterCache uses), else the encounter / sidelog context — pages such as the FT3D scene
 * host set only that, and ambience (ftSidePanelEncounterId) already reads it there. Context-first was tried
 * 2026-09-10 and broke the combat-off switch on the campaign page, so the order is deliberate.
 */
function ftMxViewerEncounterId(): string {
    const w = window as unknown as FtMxWindow & {
        ftGetEncounterContext?: () => { encounter_id?: unknown } | null;
        getSidelogContext?: () => { encounter_id?: unknown } | null;
    };
    const active = String((typeof w.ftGetActiveEncounterId === 'function' ? w.ftGetActiveEncounterId() : '') || '').trim();
    if (active) return active;
    const ctx = (typeof w.ftGetEncounterContext === 'function' ? w.ftGetEncounterContext() : null)
        || (typeof w.getSidelogContext === 'function' ? w.getSidelogContext() : null) || null;
    return String((ctx && ctx.encounter_id) || '').trim();
}

/** The WS-fed cached encounter blob, matching the id case-insensitively (a context id may differ in case). */
function ftMxCachedEncounter(encounterId: string): unknown {
    const cache = (window as unknown as FtMxWindow).FateTide?.encounterCache as
        { get?: (id: string) => unknown; keys?: () => IterableIterator<string> } | undefined;
    if (!cache || typeof cache.get !== 'function') return null;
    const direct = cache.get(encounterId) ?? cache.get(encounterId.toUpperCase());
    if (direct || typeof cache.keys !== 'function') return direct ?? null;
    const want = encounterId.toUpperCase();
    for (const key of cache.keys()) if (String(key).toUpperCase() === want) return cache.get(key) ?? null;
    return null;
}

/** A scalar off a bucketed blob (`encounters` / `campaigns` first, legacy root + [0]-wrap after). */
function ftMxScalar(arr: unknown, bucket: string, key: string): string {
    if (!arr || typeof arr !== 'object') return '';
    const rec = arr as Record<string, unknown>;
    const b = rec[bucket];
    let v: unknown = b && typeof b === 'object' && key in (b as Record<string, unknown>)
        ? (b as Record<string, unknown>)[key] : rec[key];
    if (Array.isArray(v)) v = v[0];
    if (v && typeof v === 'object') v = (v as { items?: { item_id?: unknown } }).items?.item_id;
    return v == null ? '' : String(v).trim();
}

function ftMxCampaignArr(campaignId?: string): unknown {
    const w = window as unknown as FtMxWindow;
    const ft = w.FateTide;
    const id = String(campaignId || (typeof w.ftGetCampaignId === 'function' ? w.ftGetCampaignId() : '') || ft?.campaign?.id || '').trim();
    // An explicit campaign never falls back to whatever campaign blob the page holds.
    return (id && ft?.campaignCache?.get?.(id)) || (campaignId ? null : ft?.campaignArr) || null;
}

/** The GM single track, as build_campaign_arr resolved it — only while it still names the saved pointer. */
function ftMxCampaignTrack(campaignArr: unknown): FtMusicTrack | null {
    const id = ftMxScalar(campaignArr, 'campaigns', 'campaign_encounter_mx');
    if (!id) return null;
    const t = (campaignArr as { computed?: { campaign?: { music_override_track?: unknown } } })
        ?.computed?.campaign?.music_override_track as Record<string, unknown> | undefined;
    if (!t || typeof t !== 'object' || String(t.audio_id || '').toUpperCase() !== id.toUpperCase()
        || typeof t.audio_file !== 'string' || !t.audio_file) return null;
    const name = String(t.display_name || id);
    return {
        audio_id: id, audio_file: t.audio_file, audio_name: name, display_name: name,
        audio_seconds: typeof t.audio_seconds === 'number' && t.audio_seconds > 0 ? t.audio_seconds : null,
    };
}

type FtMxRoomMusic = {
    bucket: string; combatBucket: string;
    bucketSource: '' | 'room' | 'room-type'; combatSource: '' | 'room' | 'room-type';
    /** Room / room-type buckets this catalog copy does not carry at all (an ingest newer than the page). */
    missing: string[];
};

/**
 * The listener's room's music, per field: the room's own saved bucket, else its room TYPE's default
 * (catalog `music`, skipped when the room says follow 'encounter'), else blank = the encounter's.
 */
function ftMxRoomMusic(encounterArr: unknown, encounterId: string, catalog: FtMusicCatalog): FtMxRoomMusic {
    const none: FtMxRoomMusic = { bucket: '', combatBucket: '', bucketSource: '', combatSource: '', missing: [] };
    const room = ftMxRoom;
    if (!room || !room.roomId || room.encounterId.toUpperCase() !== encounterId.toUpperCase()) return none;
    // A message from a scene frame that has since been replaced describes a scene nobody is in.
    const frame = document.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]');
    if (room.source !== window && room.source !== frame?.contentWindow) return none;
    const bucket = encounterArr && typeof encounterArr === 'object'
        ? (encounterArr as Record<string, unknown>).encounters : null;
    const raw = bucket && typeof bucket === 'object' ? (bucket as Record<string, unknown>).encounter_shape
        : (encounterArr as Record<string, unknown> | null)?.encounter_shape;
    if (typeof raw !== 'string' || raw === '') return none;
    if (!ftMxShape || ftMxShape.raw !== raw) {
        try {
            const shape = JSON.parse(raw) as { boxes?: unknown };
            ftMxShape = { raw, boxes: Array.isArray(shape?.boxes) ? shape.boxes as NonNullable<typeof ftMxShape>['boxes'] : [] };
        } catch {
            ftMxShape = { raw, boxes: [] };
        }
    }
    const box = ftMxShape.boxes.find(b => b && b.id === room.roomId);
    if (!box) return none;
    const authored = ftNormalizeRoomMusic(box.creation?.music);
    const typed = authored.follow === 'encounter' ? null : (roomTypeEntry(box.creation?.function)?.music ?? null);
    // Per field, the first candidate that can actually PLAY (rotation tracks in this catalog). An unavailable
    // room or room-type bucket falls through to the next one — ultimately the encounter's — instead of
    // silencing the room (Codex review 2026-09-10).
    const playable = (b: string | undefined): b is string => !!b && (catalog.buckets[b] || []).some(t => !t.v);
    const pick = (own: string, type: string | undefined): { bucket: string; source: '' | 'room' | 'room-type' } =>
        playable(own) ? { bucket: own, source: 'room' } : playable(type) ? { bucket: type, source: 'room-type' } : { bucket: '', source: '' };
    const ambient = pick(authored.bucket, typed?.bucket);
    const combat = pick(authored.combatBucket, typed?.combatBucket);
    const missing = [authored.bucket, authored.combatBucket, typed?.bucket ?? '', typed?.combatBucket ?? '']
        .filter(b => b !== '' && !Object.prototype.hasOwnProperty.call(catalog.buckets, b));
    return { bucket: ambient.bucket, combatBucket: combat.bucket, bucketSource: ambient.source, combatSource: combat.source, missing };
}

function ftMxOnRoomMessage(event: MessageEvent): void {
    if (event.origin !== window.location.origin || event.data?.type !== 'ft3d-room-ambience') return;
    const frame = document.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]');
    if (event.source !== window && (!frame || event.source !== frame.contentWindow)) return;
    const payload = event.data.payload as { encounterId?: unknown; roomId?: unknown } | undefined;
    if (!payload || typeof payload.encounterId !== 'string') return;
    const roomId = typeof payload.roomId === 'string' && payload.roomId ? payload.roomId : null;
    const prev = ftMxRoom;
    ftMxRoom = { encounterId: payload.encounterId, roomId, source: event.source };
    if (prev && prev.roomId === roomId && prev.encounterId === payload.encounterId && prev.source === event.source) return;
    void ftSyncViewerMusic();
}

/** What this viewer should hear, from the WS-fed caches + the catalog. Null = no encounter context. */
async function ftMxResolve(catalog: FtMusicCatalog): Promise<FtMusicResolved | null> {
    if (ftMxCreatorMode) {
        // Keyed on the CHARACTER: one sitting keeps a stable running order, a different character
        // opens on different music. Both ids may be '' (no ancestry picked yet -> general bucket).
        const orderKey = 'creator|' + (ftMxCreatorCharacter || 'anon') + '|' + ftMxCreatorAncestry;
        return ftMusicCreatorProgram(ftMxCreatorAncestryName, orderKey, catalog);
    }

    const w = window as unknown as FtMxWindow;
    const encounterId = ftMxViewerEncounterId();
    if (!encounterId || (typeof w.checkIfValidUUID === 'function' && !w.checkIfValidUUID(encounterId))) {
        ftMxLog({ phase: 'sync', skip: 'no-viewer-encounter' });
        return null;
    }
    let encounterArr: unknown = ftMxCachedEncounter(encounterId);
    if (!encounterArr && typeof w.getEncounterArr === 'function') {
        try { encounterArr = await w.getEncounterArr(encounterId); } catch { /* resolve without it */ }
    }
    const campaignArr = ftMxCampaignArr();
    const room = ftMxRoomMusic(encounterArr, encounterId, catalog);
    const resolved = ftResolveViewerMusic({
        encounterId,
        inCombat: parseInt(ftMxScalar(encounterArr, 'encounters', 'in_combat') || '0', 10) === 1,
        encounterBucket: ftMxScalar(encounterArr, 'encounters', 'encounter_mx_bucket'),
        encounterCombatBucket: ftMxScalar(encounterArr, 'encounters', 'encounter_mx_combat_bucket'),
        roomBucket: room.bucket,
        roomCombatBucket: room.combatBucket,
        roomBucketSource: room.bucketSource,
        roomCombatSource: room.combatSource,
        gmBucket: ftMxScalar(campaignArr, 'campaigns', 'campaign_encounter_mx_bucket'),
        gmTrack: ftMxCampaignTrack(campaignArr),
        localBucket: ftMxLocalBucket || '',
    }, catalog);
    // A room bucket this copy has never heard of = a newer ingest: let the sync refetch the catalog.
    if (room.missing.length) resolved.missing.push(...room.missing.filter(b => !resolved.missing.includes(b)));
    return resolved;
}

/**
 * Resolve what this viewer should be hearing and act on it.
 *
 * Triggers: page init; campaign_update where the GM override changed; the viewer's encounter changing
 * (caster-update, ft:active-encounter-change); in_combat flipping (ft:encounter-turn-state off the
 * compact stats frame); an encounter_update on the active encounter (bucket pickers, room music saves);
 * the listener crossing into another room. Resolution is local and cheap, and an unchanged answer
 * keeps the running playlist, so extra triggers cost nothing audible.
 */
async function ftSyncViewerMusic(): Promise<void> {
    try {
        if (ftMxPlaybackSuppressed()) {
            ftMxStopAll();
            ftMxLog({ phase: 'sync', skip: 'embedded-campaign-audio-owned-by-parent' });
            return;
        }
        // A resolve is already out. QUEUE this one rather than dropping it: the request that lands
        // second is the one that reflects the newest state, and dropping it strands the player on
        // stale music. Harmless for encounters (WS frames retrigger constantly, so a dropped sync
        // self-heals within a frame or two) but WRONG for the creator, where an ancestry change is
        // the ONLY trigger — pick an ancestry while the mount resolve is still in flight and the
        // music would never switch. Found by Codex review, 2026-08-26.
        // Encounter/campaign music plays only inside the FT3D system (shared/audio-surface.ts); the creator's
        // own program is the FT3D creator and is exempt.
        if (!ftMxCreatorMode && !ftCampaignAudioSurface()) {
            ftMxLog({ phase: 'sync', skip: 'not-ft3d-surface' });
            return;
        }
        if (ftMxInFlight) { ftMxResyncQueued = true; return; }
        // Paused wins over every trigger. Set a new local theme or hit resume to come back.
        if (ftMxPaused) { ftMxLog({ phase: 'sync', skip: 'paused' }); return; }
        // No creator program and no encounter in view (login, account, catalog pages all carry the
        // decks): nothing can resolve, so do not pull the catalog either.
        if (!ftMxCreatorMode) {
            const w = window as unknown as FtMxWindow;
            const id = ftMxViewerEncounterId();
            if (!id || (typeof w.checkIfValidUUID === 'function' && !w.checkIfValidUUID(id))) {
                ftMxLog({ phase: 'sync', skip: 'no-viewer-encounter' });
                return;
            }
        }
        ftMxInFlight = true;
        let resolved: FtMusicResolved | null = null;
        try {
            // The catalog is the only network read, once per catalog revision (browser-cached).
            let catalog = await ftMusicCatalog();
            if (!catalog) { ftMxLog({ phase: 'sync', skip: 'catalog-unavailable' }); return; }
            // A MusicForge ingest since this page loaded: the campaign push names the new revision.
            if (!ftMxCreatorMode) {
                const pushedRev = String((ftMxCampaignArr() as { computed?: { campaign?: { music_catalog_rev?: unknown } } } | null)
                    ?.computed?.campaign?.music_catalog_rev || '');
                if (pushedRev && pushedRev !== catalog.rev) catalog = (await ftMusicCatalogEnsureRev(pushedRev)) || catalog;
            }
            resolved = await ftMxResolve(catalog);
            // Saved state naming a bucket this copy lacks = an ingest newer than the page. Refetch
            // once (throttled) and resolve again; until then 'none'/empty keeps the music running.
            if (resolved && resolved.missing.length) {
                const fresh = await ftMusicCatalogRefreshOnMiss();
                if (fresh) { catalog = fresh; resolved = await ftMxResolve(catalog); }
            }
        } finally {
            ftMxInFlight = false;
        }
        if (!resolved) return;

        // 'none' means KEEP PLAYING — never cut to silence just because the next room has
        // no music authored. Same rule matchCampaignAudio() follows for a blank ambience id.
        if (resolved.mode === 'none' || !resolved.tracks || !resolved.tracks.length) {
            ftMxLog({ phase: 'sync', mode: resolved.mode, action: 'keep-current' });
            ftMxAnnounce();
            return;
        }

        // track_count too: a catalog revision that grows or shrinks the same bucket must install the new list.
        const key = resolved.mode + '|' + resolved.order_key + '|' + (resolved.bucket || '') + '|' + resolved.track_count;
        ftMxLastResolved = resolved;
        if (key === ftMxKey) {
            ftMxLog({ phase: 'sync', action: 'unchanged', key });
            return;    // same playlist — do NOT restart it
        }
        ftMxKey = key;
        if (ftMxSuspended) {          // GM is previewing; take effect when they stop
            ftMxTracks = resolved.tracks;
            ftMxCrossfadeMs = resolved.crossfade_ms || 6000;
            // Order first, THEN the index off it — a bare `= 0` here would leave the cursor and
            // the index disagreeing, so the first advance after the preview ends would jump.
            ftMxBuildOrder(-1);
            ftMxIndex = ftMxOrder[0] ?? 0;
            ftMxLog({ phase: 'sync', action: 'deferred-suspended', key });
            return;
        }
        ftMxStart(resolved);
    } catch (e) {
        ftMxLog({ phase: 'sync', error: String(e) });
    } finally {
        // Drain a sync that arrived while this one held the lock. setTimeout(0) rather than a direct
        // call so the queued run starts on a clean stack with ftMxInFlight already cleared, and so a
        // pathological trigger storm cannot recurse — each drain services the LATEST state, and any
        // further triggers during it simply re-arm the flag.
        if (ftMxResyncQueued && !ftMxInFlight) {
            ftMxResyncQueued = false;
            setTimeout(() => { void ftSyncViewerMusic(); }, 0);
        }
    }
}

function ftMxInit(): void {
    try {
        if (!ftMxDeck(0) || !ftMxDeck(1)) return;   // page without the soundboard
        if (ftMxPlaybackSuppressed()) {
            ftMxStopAll();
            ftMxLog({ phase: 'init', skip: 'embedded-campaign-audio-owned-by-parent' });
            return;
        }

        // Restore a local override from this tab's session (see ftMxLocalBucket).
        try {
            const saved = sessionStorage.getItem('ft_mx_local');
            if (saved) ftMxLocalBucket = saved;
            if (sessionStorage.getItem('ft_mx_paused') === '1') ftMxPaused = true;
            // Restored BEFORE the first resolve, so a shuffling listener's very first playlist
            // is already shuffled rather than starting in order until they touch the button.
            if (sessionStorage.getItem('ft_mx_shuffle') === '1') ftMxShuffle = true;
        } catch (e) { /* ignore */ }

        // Master volume/mute governs the decks. Re-apply on every change, mid-fade included.
        const master = ftMxMaster();
        if (master) {
            master.addEventListener('volumechange', () => ftMxApplyGain());
            // GM previewing a track on music_1 (soundboard) or an `mx` roll landing there:
            // duck out of the way rather than stacking two pieces of music.
            master.addEventListener('play', () => {
                ftMxSuspended = true;
                ftMxLog({ phase: 'suspend', reason: 'master-playing' });
                ftMxStopAll();
            });
            const resume = () => {
                if (!ftMxSuspended) return;
                ftMxSuspended = false;
                ftMxLog({ phase: 'resume' });
                ftMxKey = '';           // force a fresh resolve
                void ftSyncViewerMusic();
            };
            master.addEventListener('pause', resume);
            master.addEventListener('ended', resume);
        }

        // Safety net: if the scheduled handoff was missed (tab throttling, bad duration),
        // 'ended' still advances the playlist.
        for (let i = 0; i < FT_MX_DECK_IDS.length; i++) {
            const d = ftMxDeck(i);
            if (!d) continue;
            d.addEventListener('ended', () => {
                // ⚠ MUST stay inert during a normal crossfade. The fade starts at
                // (duration - crossfade) and runs exactly that long, so the outgoing track
                // reaches its natural end just as the fade lands — 'ended' therefore fires on
                // EVERY transition, not only on a missed handoff.
                //
                // `i !== ftMxActive` does NOT cover this: ftMxActive still points at the
                // OUTGOING deck until the fade completes, so the guard passed and a second
                // ftMxAdvance() ran — skipping a track and restarting a fade from a deck that
                // was still loading, which is what blanked the now-playing strip mid-transition.
                if (ftMxIncoming !== null) return;          // a handover is already in flight
                if (i !== ftMxActive || ftMxSuspended || ftMxPaused) return;
                if (ftMxTracks.length < 2) return;
                ftMxLog({ phase: 'ended-fallback' });
                ftMxAdvance();
            });
        }

        // State that can move the answer, beyond the direct calls in campaign-update / caster-update.
        // Before 2026-09-10 nothing re-resolved on a combat flip, so combat music never switched live.
        const activeEncounter = (): string => ftMxViewerEncounterId().toUpperCase();
        const onActiveEncounterEvent = (ev: Event): void => {
            const id = String((ev as CustomEvent<{ encounterId?: unknown }>).detail?.encounterId || '').trim().toUpperCase();
            if (id && id === activeEncounter()) void ftSyncViewerMusic();
        };
        window.addEventListener('ft:encounter-turn-state', onActiveEncounterEvent);      // in_combat on/off
        window.addEventListener('ft:encounter-state-received', onActiveEncounterEvent);  // buckets, room saves
        window.addEventListener('ft:active-encounter-change', () => { void ftSyncViewerMusic(); });
        window.addEventListener('message', ftMxOnRoomMessage);                           // listener changed room

        // Now-playing strip: delegated so it survives any re-render of the sidelog.
        document.addEventListener('click', (ev) => {
            const t = ev.target as HTMLElement | null;
            if (!t || typeof t.closest !== 'function') return;
            const pick = t.closest('[data-ft-mx-track]') as HTMLElement | null;
            if (pick) {
                ftMusicPlayIndex(parseInt(pick.getAttribute('data-ft-mx-track') || '0', 10));
                ftMxUpdateNowPlaying();
                return;
            }
            const ctl = t.closest('[data-ft-mx-ctl]') as HTMLElement | null;
            if (!ctl) return;
            const which = ctl.getAttribute('data-ft-mx-ctl');
            if (which === 'toggle') {
                if (ftMxPaused) ftMusicResume(); else ftMusicPause();
            } else if (which === 'skip') {
                ftMusicSkip();
            } else if (which === 'prev') {
                ftMusicPrev();
            } else if (which === 'loop') {
                ftMusicToggleLoop();
            } else if (which === 'shuffle') {
                ftMusicToggleShuffle();
            } else if (which === 'seek') {
                const r = ctl.getBoundingClientRect();
                const x = (ev as MouseEvent).clientX;
                if (r.width > 0 && typeof x === 'number') ftMusicSeekFraction((x - r.left) / r.width);
            }
            ftMxUpdateNowPlaying();
        });
        // 1 Hz is plenty for a progress bar and avoids a timeupdate handler firing ~4x/sec
        // per deck while two of them are crossfading.
        setInterval(ftMxUpdateNowPlaying, 1000);

        ftMxApplyGain();
        void ftSyncViewerMusic();
    } catch (e) {
        ftMxLog({ phase: 'init', error: String(e) });
    }
}

/**
 * Fade the decks down and stop. Ramps from whatever each deck is currently at, so pausing
 * mid-crossfade doesn't jump. A hard pause() on music is jarring in a way it isn't on SFX —
 * the whole point of the 10 s crossfade is that music never starts or stops abruptly.
 */
function ftMusicPause(fadeMs?: number): void {
    const ms = typeof fadeMs === 'number' && fadeMs > 0 ? fadeMs : 1500;
    ftMxPaused = true;
    try { sessionStorage.setItem('ft_mx_paused', '1'); } catch (e) { /* ignore */ }
    ftMxClearTimers();

    const from = [ftMxGain[0] ?? 0, ftMxGain[1] ?? 0];
    const steps = Math.max(1, Math.round(ms / FT_MX_FADE_TICK_MS));
    let step = 0;
    ftMxLog({ phase: 'pause', fadeMs: ms });

    ftMxFadeTimer = setInterval(() => {
        step++;
        const p = Math.min(1, step / steps);
        const k = Math.cos((p * Math.PI) / 2);       // equal-power taper, same curve as the crossfade
        ftMxGain[0] = (from[0] ?? 0) * k;
        ftMxGain[1] = (from[1] ?? 0) * k;
        ftMxApplyGain();
        if (p >= 1) {
            if (ftMxFadeTimer !== null) { clearInterval(ftMxFadeTimer); ftMxFadeTimer = null; }
            ftMxStopAll();
            ftMxUpdateNowPlaying();
            ftMxAnnounce();
        }
    }, FT_MX_FADE_TICK_MS);
    ftMxUpdateNowPlaying();
}

/** Resume: re-resolve from scratch so we pick up anything that changed while paused. */
function ftMusicResume(): void {
    ftMxPaused = false;
    try { sessionStorage.removeItem('ft_mx_paused'); } catch (e) { /* ignore */ }
    ftMxKey = '';
    ftMxLog({ phase: 'resume-from-pause' });
    ftMxAnnounce();
    void ftSyncViewerMusic();
}

function ftMusicIsPaused(): boolean { return ftMxPaused; }
function ftMusicCurrentBucket(): string | null { return ftMxCurrentBucket; }

/** Previous track. Crossfades like everything else. */
function ftMusicPrev(): void {
    if (ftMxPaused || ftMxTracks.length < 2) return;
    ftMxLog({ phase: 'prev' });
    // Just -1. The old "-2 on the index, then advance(1)" dance existed because repeat-one used
    // to override an explicit step; now it doesn't, and rewinding the INDEX by hand would be
    // wrong anyway — under shuffle the previous track is the previous entry in the play ORDER,
    // which the index alone cannot express.
    ftMxAdvance(-1);
}

function ftMusicToggleLoop(): boolean {
    ftMxLoopOne = !ftMxLoopOne;
    ftMxLog({ phase: 'loop', on: ftMxLoopOne });
    ftMxAnnounce();
    // Re-arm the handoff so the change takes effect on THIS track, not the next one.
    ftMxScheduleNext();
    ftMxUpdateNowPlaying();
    return ftMxLoopOne;
}
function ftMusicIsLooping(): boolean { return ftMxLoopOne; }

/**
 * Shuffle THIS viewer's play order. Local only — writes nothing, broadcasts nothing, exactly
 * like the "just me" theme override, so one player shuffling does not reorder the table.
 *
 * Rebuilt around the current track, so the button is safe to hit mid-song: what is playing
 * carries on and only what follows changes. No re-arm needed — the handoff is scheduled off
 * POSITION in the current track, and that track has not moved.
 */
function ftMusicToggleShuffle(): boolean {
    ftMxShuffle = !ftMxShuffle;
    ftMxBuildOrder(ftMxIndex);
    ftMxLog({ phase: 'shuffle', on: ftMxShuffle });
    // sessionStorage, matching ft_mx_local / ft_mx_paused above: how you are listening right
    // now, not a saved account setting. Dies with the tab like its two siblings.
    try {
        if (ftMxShuffle) sessionStorage.setItem('ft_mx_shuffle', '1');
        else sessionStorage.removeItem('ft_mx_shuffle');
    } catch (e) { /* ignore */ }
    ftMxUpdateNowPlaying();
    return ftMxShuffle;
}
function ftMusicIsShuffling(): boolean { return ftMxShuffle; }

/**
 * Seek within the current track. `f` is 0..1 across the progress bar.
 * Re-arms the pending handoff, because the crossfade is scheduled off POSITION -- without
 * that, seeking near the end would leave the old timer to fire far too late (or a seek
 * backwards would hand over almost immediately).
 */
function ftMusicSeekFraction(f: number): void {
    const deck = ftMxDeck(ftMxIncoming ?? ftMxActive);
    const track = ftMxTracks[ftMxIndex];
    if (!deck || !track || ftMxPaused) return;
    const dur = (track.audio_seconds && track.audio_seconds > 0)
        ? track.audio_seconds
        : (isFinite(deck.duration) && deck.duration > 0 ? deck.duration : 0);
    if (dur <= 0) return;
    const target = Math.max(0, Math.min(dur - 0.25, (isFinite(f) ? f : 0) * dur));
    try { deck.currentTime = target; } catch (e) { return; }   // still loading: ignore
    ftMxLog({ phase: 'seek', to: Math.round(target) });
    ftMxScheduleNext();
    ftMxUpdateNowPlaying();
}

/** Next track now. Still crossfades — a hard cut is exactly what the fade exists to avoid. */
function ftMusicSkip(): void {
    if (ftMxPaused || ftMxTracks.length < 2) return;
    ftMxLog({ phase: 'skip' });
    ftMxAdvance(1);   // explicit: Next must move even while repeat-one is on
}

/** "Just me": play a theme for THIS viewer only. Writes nothing, broadcasts nothing. */
function ftMusicSetLocal(bucket: string): void {
    // Choosing a theme is an unmistakable "play this" — lift a pause rather than silently
    // doing nothing.
    if (ftMxPaused) {
        ftMxPaused = false;
        try { sessionStorage.removeItem('ft_mx_paused'); } catch (e) { /* ignore */ }
    }
    ftMxLocalBucket = String(bucket || '').trim() || null;
    try {
        if (ftMxLocalBucket) sessionStorage.setItem('ft_mx_local', ftMxLocalBucket);
        else sessionStorage.removeItem('ft_mx_local');
    } catch (e) { /* private mode — the override just won't survive a reload */ }
    ftMxKey = '';                       // force a switch even if the bucket matches the campaign's
    ftMxLog({ phase: 'local-set', bucket: ftMxLocalBucket });
    void ftSyncViewerMusic();
}

/** Drop the local override and fall back to what everyone else is hearing. */
function ftMusicClearLocal(): void {
    ftMxLocalBucket = null;
    try { sessionStorage.removeItem('ft_mx_local'); } catch (e) { /* ignore */ }
    ftMxKey = '';
    ftMxLog({ phase: 'local-clear' });
    void ftSyncViewerMusic();
}

function ftMusicLocalBucket(): string | null { return ftMxLocalBucket; }

/**
 * Adopt a GM music write THIS tab just made (set_campaign_music_inc.php answered ok) into the cached
 * campaign blob, ahead of its campaign_update echo. Resolution reads that cache, so without this the
 * immediate re-resolve would replay the OLD theme until the echo lands. '' = back to auto. The echo
 * then carries the same values and resolves to the playlist already running.
 */
function ftMusicAdoptCampaignOverride(bucket: string, campaignId?: string, token?: unknown): boolean {
    try {
        const arr = ftMxCampaignArr(campaignId) as {
            campaigns?: Record<string, unknown>; computed?: { campaign?: Record<string, unknown> };
        } | null;
        // A campaign push replaced the cached blob after the write was sent. The set endpoint publishes
        // BEFORE it answers, so that push carries this write or a newer one — adopting the older HTTP
        // answer over it would clobber newer state (Codex review 2026-09-10).
        if (token !== undefined && arr !== token) { ftMxLog({ phase: 'adopt-override', skip: 'newer-push' }); return false; }
        if (!arr || !arr.campaigns || typeof arr.campaigns !== 'object') return false;
        const b = String(bucket || '').trim();
        if (b) arr.campaigns.campaign_encounter_mx_bucket = b;
        else delete arr.campaigns.campaign_encounter_mx_bucket;
        delete arr.campaigns.campaign_encounter_mx;            // a theme replaces any single track
        if (arr.computed?.campaign) delete arr.computed.campaign.music_override_track;
        return true;
    } catch (e) {
        ftMxLog({ phase: 'adopt-override', error: String(e) });
        return false;
    }
}

/** The cached campaign blob as it stands now — an opaque token the music form hands back to adoption. */
function ftMusicCampaignToken(campaignId?: string): unknown {
    return ftMxCampaignArr(campaignId);
}

/**
 * Turn on the character-creator program and (re)resolve for `ancestryId`.
 *
 * Called on creator mount with '' and again on every ancestry change. Passing the SAME ancestry
 * twice is a no-op at the playlist level: the resolve returns the same order_key, ftMxKey matches,
 * and the running track is left alone — so the re-entrant `change` events the wizard fires during
 * its AJAX cascade cannot restart the music.
 */
function ftMusicStartCreator(ancestryId?: string, characterId?: string, ancestryName?: string): void {
    const nextAncestry = String(ancestryId || '').trim();
    const nextCharacter = String(characterId || '').trim();
    const nextName = nextAncestry ? String(ancestryName || '').trim() : '';
    const wasCreator = ftMxCreatorMode;
    ftMxCreatorMode = true;
    // A creator session is not an encounter, so a stale "just me" bucket from some other tab's
    // sessionStorage must not ride along into the creator's resolve.
    ftMxLocalBucket = null;
    // Nor may a pause left over from an encounter EARLIER IN THIS TAB silently swallow the creator's
    // music — ftMxInit restores ft_mx_paused from sessionStorage, and ftSyncViewerMusic returns early
    // while paused, so without this the console would sit on "Tuning…" forever with no way back.
    // Opening the creator with music enabled is an unmistakable "play this", same reasoning as
    // ftMusicSetLocal's pause lift.
    if (ftMxPaused) {
        ftMxPaused = false;
        try { sessionStorage.removeItem('ft_mx_paused'); } catch (e) { /* ignore */ }
    }
    if (wasCreator && nextAncestry === ftMxCreatorAncestry && nextCharacter === ftMxCreatorCharacter
        && nextName === ftMxCreatorAncestryName) {
        void ftSyncViewerMusic();   // ftMxKey will match; this is the cheap no-op path
        return;
    }
    ftMxCreatorAncestry = nextAncestry;
    ftMxCreatorCharacter = nextCharacter;
    ftMxCreatorAncestryName = nextName;
    ftMxLog({ phase: 'creator-set', ancestry: nextAncestry || '(none)' });
    void ftSyncViewerMusic();
}

/** Leave creator mode. Stops playback — there is no encounter here to fall back to. */
function ftMusicStopCreator(): void {
    if (!ftMxCreatorMode) return;
    ftMxCreatorMode = false;
    ftMxCreatorAncestry = '';
    ftMxCreatorCharacter = '';
    ftMxCreatorAncestryName = '';
    ftMxKey = '';
    ftMxStopAll();
    ftMxLog({ phase: 'creator-clear' });
    ftMxAnnounce();
}

/**
 * What is playing right now, for any console that wants to render it.
 *
 * Returns null when nothing is loaded. `slot`/`bucket` are populated only under the creator
 * program; an encounter playlist leaves them undefined.
 */
function ftMusicNowPlaying(): {
    name: string; slot: string; bucket: string; index: number; total: number; paused: boolean;
} | null {
    const track = ftMxTracks[ftMxIndex];
    if (!track) return null;
    return {
        name:   String(track.display_name || track.audio_name || ''),
        slot:   String(track.program_slot || ''),
        bucket: String(track.program_bucket || ''),
        index:  ftMxIndex,
        total:  ftMxTracks.length,
        paused: ftMxPaused,
    };
}

/**
 * The GM theme picker's rows, straight from the music catalog the page already holds — one row per
 * bucket. Replaced the `window.FT_MUSIC_THEMES` island, which ran a whole-table GROUP BY over `audio` on
 * every campaign page load (2026-09-10).
 */
async function ftMusicThemeRows(): Promise<Array<{ bucket: string; vocal: boolean; count: number; minutes: number }>> {
    const catalog = await ftMusicCatalog();
    if (!catalog) return [];
    return Object.keys(catalog.buckets).sort().map((bucket) => {
        const rows = catalog.buckets[bucket] || [];
        const seconds = rows.reduce((sum, t) => sum + (t.s || 0), 0);
        return { bucket, vocal: rows.some(t => !!t.v), count: rows.length, minutes: Math.round(seconds / 60) };
    });
}

/**
 * The resolver's last playable answer, without the track bodies — what a console or a smoke needs to
 * say WHY this is playing (mode, bucket, order key, and for the creator its program + buckets). Null
 * until something resolved. Read-only; the playlist itself stays private to the player.
 */
function ftMusicResolvedState(): Record<string, unknown> | null {
    const r = ftMxLastResolved;
    if (!r) return null;
    return {
        mode: r.mode, bucket: r.bucket, order_key: r.order_key, track_count: r.track_count, reason: r.reason,
        program: r.program ?? null, ancestry_name: r.ancestry_name ?? null,
        ancestry_bucket: r.ancestry_bucket ?? null, vocal_bucket: r.vocal_bucket ?? null,
        slots: r.tracks.map(t => t.program_slot ?? null),
    };
}

// Public surface — the WS handlers call window.ftSyncViewerMusic(); the music console calls
// the local helpers.
(window as unknown as Record<string, unknown>).ftSyncViewerMusic = ftSyncViewerMusic;
(window as unknown as Record<string, unknown>).ftMusicStopAll = ftMxStopAll;
(window as unknown as Record<string, unknown>).ftMusicSetLocal = ftMusicSetLocal;
(window as unknown as Record<string, unknown>).ftMusicClearLocal = ftMusicClearLocal;
(window as unknown as Record<string, unknown>).ftMusicLocalBucket = ftMusicLocalBucket;
(window as unknown as Record<string, unknown>).ftMusicAdoptCampaignOverride = ftMusicAdoptCampaignOverride;
(window as unknown as Record<string, unknown>).ftMusicCampaignToken = ftMusicCampaignToken;
(window as unknown as Record<string, unknown>).ftMusicPause = ftMusicPause;
(window as unknown as Record<string, unknown>).ftMusicResume = ftMusicResume;
(window as unknown as Record<string, unknown>).ftMusicIsPaused = ftMusicIsPaused;
(window as unknown as Record<string, unknown>).ftMusicSkip = ftMusicSkip;
(window as unknown as Record<string, unknown>).ftMusicCurrentBucket = ftMusicCurrentBucket;
(window as unknown as Record<string, unknown>).ftMusicPrev = ftMusicPrev;
(window as unknown as Record<string, unknown>).ftMusicToggleLoop = ftMusicToggleLoop;
(window as unknown as Record<string, unknown>).ftMusicIsLooping = ftMusicIsLooping;
(window as unknown as Record<string, unknown>).ftMusicToggleShuffle = ftMusicToggleShuffle;
(window as unknown as Record<string, unknown>).ftMusicIsShuffling = ftMusicIsShuffling;
(window as unknown as Record<string, unknown>).ftMusicSeekFraction = ftMusicSeekFraction;
(window as unknown as Record<string, unknown>).ftMusicPlayIndex = ftMusicPlayIndex;
// Character-creator program (2026-08-26) — see ftMusicStartCreator.
(window as unknown as Record<string, unknown>).ftMusicStartCreator = ftMusicStartCreator;
(window as unknown as Record<string, unknown>).ftMusicStopCreator = ftMusicStopCreator;
(window as unknown as Record<string, unknown>).ftMusicNowPlaying = ftMusicNowPlaying;
(window as unknown as Record<string, unknown>).ftMusicResolvedState = ftMusicResolvedState;
(window as unknown as Record<string, unknown>).ftMusicThemeRows = ftMusicThemeRows;

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ftMxInit);
} else {
    ftMxInit();
}

export { ftSyncViewerMusic };
